import * as React from 'react';
import { persistCredentialSession, isSSR } from '../../../utils/network/utils';
import { CognitoErrorCodesEnum } from '../../../utils/types/eunms';
import { useCognitoContext } from '../../providers/cognito.provider';
import type { LoginFormValues } from '@components/organisms/login-form/useLoginForm';
import type { QueryParamsErrorCodesEnum, ResponseResult } from '@utils';
import { LOGOUT_ERROR_KEY } from '@utils';

export enum LoginStepEnum {
	LOGIN = 'login',
	RESET_PASSWORD = 'reset-password',
	CHOOSE_EMAIL = 'choose-email',
	CHANGE_TEMP_PASSWORD = 'change-temp-password',
}

type Props = {
	onSuccess: (data: unknown) => Promise<void> | void;
};

export const useLogin = ({ onSuccess }: Props) => {
	const { credentialsLogin, changePassword } = useCognitoContext();

	const [step, setStep] = React.useState<LoginStepEnum>(LoginStepEnum.LOGIN);
	const [loginEmail, setLoginEmail] = React.useState<string | undefined>();

	const handleLogin = React.useCallback(
		async (data: LoginFormValues): Promise<ResponseResult<unknown>> => {
			const response = await credentialsLogin(data.email, data.password);
			const normalizedEmail = data.email.trim();

			if (response.error?.code === CognitoErrorCodesEnum.PASSWORD_RESET_REQUIRED) {
				setLoginEmail(normalizedEmail);
				setStep(LoginStepEnum.CHANGE_TEMP_PASSWORD);
			} else if (response.error?.code === CognitoErrorCodesEnum.INVALID_EMAIL_OR_PASSWORD) {
				setLoginEmail(normalizedEmail);
			}

			if (response.error) {
				return response;
			}

			const persistError = persistCredentialSession(response.result);
			if (persistError) {
				return persistError;
			}

			await onSuccess(response.result);
			return response;
		},
		[credentialsLogin, onSuccess]
	);

	const handleChangePassword = React.useCallback(
		async (values: { password: string }) => {
			const response = await changePassword(loginEmail!, values.password);

			if (response.error) {
				return response;
			}

			const persistError = persistCredentialSession(response.result);
			if (persistError) {
				return persistError;
			}

			await onSuccess(response.result);
			return response;
		},
		[loginEmail, changePassword, onSuccess]
	);

	const navigateToForgotPassword = React.useCallback((email?: string) => {
		const trimmed = email?.trim();
		if (trimmed) {
			setLoginEmail(trimmed);
		}
		setStep(LoginStepEnum.RESET_PASSWORD);
	}, []);

	const changeStep = (nextStep: LoginStepEnum) => setStep(nextStep);

	const errorCode: QueryParamsErrorCodesEnum | undefined = React.useMemo(() => {
		if (isSSR()) {
			return;
		}

		const storedErrorCode: string | null = sessionStorage?.getItem(LOGOUT_ERROR_KEY);

		sessionStorage?.removeItem(LOGOUT_ERROR_KEY);

		return storedErrorCode as QueryParamsErrorCodesEnum;
	}, []);

	return {
		step,
		handleLogin,
		handleChangePassword,
		changeStep,
		navigateToForgotPassword,
		loginEmail,
		errorCode,
	};
};
